import React, { Suspense } from 'react';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import { createBrowserHistory } from 'history';
import AppLayout from './layout/AppLayout';
import SkeletonPage from './components/SkeletonPage';
import DocumentTitle from './components/DocumentTitle';
import PageTransition from './components/PageTransition';
import KeyboardShortcuts from './components/KeyboardShortcuts';
import ErrorBoundary from './components/ErrorBoundary';
import { fetchGoodsPrices } from './common/util/goodsPrices';
import { applyWebLoginConfig } from './common/util/authConfig';
import errorLogger from './common/util/errorLogger';

const Dashboard = React.lazy(() => import('./pages/dashboard/Dashboard'));
const LoginPage = React.lazy(() => import('./pages/uLogin/uLoginPage'));
const GuidePage = React.lazy(() => import('./pages/guide/GuidePage'));
const NewTimeApp = React.lazy(() => import('./pages/newTime/NewTimeApp'));
const NewBillApp = React.lazy(() => import('./pages/newBill/NewBillApp'));
const NewWriteApp = React.lazy(() => import('./pages/newWrite/NewWriteApp'));
const NewWriteArticleApp = React.lazy(() => import('./pages/newWrite/NewWriteArticleApp'));
const NewWriteReadApp = React.lazy(() => import('./pages/newWrite/NewWriteReadApp'));
const NewWriteLiveApp = React.lazy(() => import('./pages/newWrite/NewWriteLiveApp'));
const NewWriteLive100App = React.lazy(() => import('./pages/newWrite/NewWriteLive100App'));
const NewWritePublishApp = React.lazy(() => import('./pages/newWrite/NewWritePublishApp'));
const AiChatApp = React.lazy(() => import('./pages/aiChat/AiChatApp'));
const ShenjiEmbedPage = React.lazy(() => import('./pages/shenji/ShenjiEmbedPage'));
const CommonHelpPage = React.lazy(() => import('./pages/help/CommonHelpPage'));
const FeedbackPage = React.lazy(() => import('./pages/feedback/FeedbackPage'));
const PayPage = React.lazy(() => import('./pages/pay/PayPage'));
const PaySuccess = React.lazy(() => import('./pages/pay/PaySuccess'));
const PayFail = React.lazy(() => import('./pages/pay/PayFail'));

if (window.location.hostname === 'localhost') {
  window.addEventListener('unhandledrejection', (event) => {
    event.preventDefault();
    errorLogger.warn('SJ-APP-004', 'dev unhandled rejection suppressed', {
      reasonMessage: event.reason?.message || event.reason,
    });
  });
}

if (window.location.hash && window.location.hash.startsWith('#/')) {
  const hashRoute = window.location.hash.slice(1);
  const hashQueryIndex = hashRoute.indexOf('?');
  const hashPath = hashQueryIndex >= 0 ? hashRoute.slice(0, hashQueryIndex) : hashRoute;
  const hashSearch = hashQueryIndex >= 0 ? hashRoute.slice(hashQueryIndex) : window.location.search;
  window.history.replaceState(null, '', `${hashPath}${hashSearch}`);
}

if (window.location.pathname === '/app/user-key' || window.location.pathname === '/app/user-key/') {
  window.location.replace(`/user-key.html${window.location.search}${window.location.hash}`);
}

const history = createBrowserHistory();
if (!window.app) {
  window.app = {};
}
window.app.history = history;
const isAiChatRoute = window.location.pathname.startsWith('/ai-chat');
applyWebLoginConfig({
  appId: window.app.appId,
  biz: window.app.biz || (isAiChatRoute ? 'aiChat' : 'yuxie'),
  name: window.app.name || (isAiChatRoute ? 'AI 对话' : '用户中心'),
  serviceAppId: window.app.serviceAppId || (isAiChatRoute ? 'wx52231a5da8c7596d' : undefined),
});

class App extends React.Component {
  componentDidMount() {
    window.__DASHBOARD_MOUNTED__ = true;
    fetchGoodsPrices();
  }
  render() {
    return (
      <BrowserRouter>
        <KeyboardShortcuts />
        <DocumentTitle />
        <Suspense fallback={<SkeletonPage loading rows={12} />}>
          <ErrorBoundary>
            <Switch>
              <Route path="/login" component={LoginPage} />
              <Route path="/guide/:product" component={GuidePage} />
              <Route>
                <AppLayout>
                  <Switch>
                    <Route exact path="/" render={(props) => <PageTransition><Dashboard {...props} /></PageTransition>} />
                    <Route exact path="/index.html" render={(props) => <PageTransition><Dashboard {...props} /></PageTransition>} />
                    <Route exact path="/time" render={(props) => <PageTransition><NewTimeApp {...props} /></PageTransition>} />
                    <Route exact path="/bill" render={(props) => <PageTransition><NewBillApp {...props} /></PageTransition>} />
                    <Route exact path="/write" render={(props) => <PageTransition><NewWriteApp {...props} /></PageTransition>} />
                    <Route exact path="/write-article" render={(props) => <PageTransition><NewWriteArticleApp {...props} /></PageTransition>} />
                    <Route exact path="/write-read" render={(props) => <PageTransition><NewWriteReadApp {...props} /></PageTransition>} />
                    <Route exact path="/write-live" render={(props) => <PageTransition><NewWriteLiveApp {...props} /></PageTransition>} />
                    <Route exact path="/write-live100" render={(props) => <PageTransition><NewWriteLive100App {...props} /></PageTransition>} />
                    <Route exact path="/write-publish" render={(props) => <PageTransition><NewWritePublishApp {...props} /></PageTransition>} />
                    <Route path="/ai-chat" render={(props) => <PageTransition><AiChatApp {...props} /></PageTransition>} />
                    <Route exact path="/shenji.html" render={(props) => <PageTransition><ShenjiEmbedPage {...props} /></PageTransition>} />
                    <Route exact path="/help" render={(props) => <PageTransition><CommonHelpPage {...props} /></PageTransition>} />
                    <Route exact path="/feedback" render={(props) => <PageTransition><FeedbackPage {...props} /></PageTransition>} />
                    <Route path="/pay/success" render={(props) => <PageTransition><PaySuccess {...props} /></PageTransition>} />
                    <Route path="/pay/fail" render={(props) => <PageTransition><PayFail {...props} /></PageTransition>} />
                    <Route path="/pay/:biz" render={(props) => <PageTransition><PayPage {...props} /></PageTransition>} />
                  </Switch>
                </AppLayout>
              </Route>
            </Switch>
          </ErrorBoundary>
        </Suspense>
      </BrowserRouter>
    );
  }
}

export default App;
